import { useCallback, useMemo } from 'react';

// Type definitions (replacing ba.components imports)
export enum WebAddinPlatforms {
  OutlookForMac = 'OutlookForMac',
  Outlook2016 = 'Outlook2016',
  DesktopClient = 'DesktopClient',
  OWA = 'OWA'
}

export interface IPlatformService {
  IsSetSupported(name: string, minVersion?: string): boolean;
  GetDateTimeOffset(): number;
  GetCurrentPlatform(): WebAddinPlatforms | null;
  GetPlatformString(): string;
  closeContainer(): void;
}

// Utility function to replace ConvertUtils.ToInt
const toInt = (value: string): number => {
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? 0 : parsed;
};

// Hook for platform-related functionality
export const usePlatform = () => {
  // Check if a specific Office.js set is supported
  const isSetSupported = useCallback((name: string, minVersion?: string): boolean => {
    return Office.context.requirements.isSetSupported(name, minVersion);
  }, []);

  // Get current timezone offset
  const getDateTimeOffset = useCallback((): number => {
    return Office.context.mailbox.convertToLocalClientTime(new Date()).timezoneOffset;
  }, []);

  // Get current platform
  const getCurrentPlatform = useCallback((): WebAddinPlatforms | null => {
    if (Office.context.diagnostics) {
      const currentPlatforms = Office.context.diagnostics;
      if (currentPlatforms.host === Office.HostType.Outlook) {
        if (currentPlatforms.platform === Office.PlatformType.Mac) {
          return WebAddinPlatforms.OutlookForMac;
        } else {
          if (currentPlatforms.platform === Office.PlatformType.PC) {
            const version = currentPlatforms.version;
            const versionArr = version.split('.');
            if (toInt(versionArr[0]) === 16) {
              return WebAddinPlatforms.Outlook2016;
            }
            return WebAddinPlatforms.DesktopClient;
          }
          if (currentPlatforms.platform === Office.PlatformType.OfficeOnline) {
            return WebAddinPlatforms.OWA;
          }
        }
      }
    } else {
      const currentPlatforms = Office.context.mailbox.diagnostics;
      if (currentPlatforms) {
        const version = currentPlatforms.hostVersion;
        const versionArr = version.split('.');
        if (toInt(versionArr[0]) === 16) {
          return WebAddinPlatforms.Outlook2016;
        }
      }
    }
    return null;
  }, []);

  // Get platform string
  const getPlatformString = useCallback((): string => {
    if (Office.context.diagnostics) {
      const currentPlatforms = Office.context.diagnostics;
      if (currentPlatforms.host === Office.HostType.Outlook) {
        if (currentPlatforms.platform === Office.PlatformType.Mac) {
          return "OutlookForMac";
        } else {
          if (currentPlatforms.platform === Office.PlatformType.PC) {
            return "OutlookBuild " + currentPlatforms.version;
          }
          if (currentPlatforms.platform === Office.PlatformType.OfficeOnline) {
            return "OfficeOnline";
          }
        }
      }
    } else {
      const currentPlatforms = Office.context.mailbox.diagnostics;
      if (currentPlatforms) {
        return "OutlookBuild " + currentPlatforms.hostVersion;
      }
    }
    return "";
  }, []);

  // Close container
  const closeContainer = useCallback((): void => {
    Office.context.ui.closeContainer();
  }, []);

  // Memoized values for better performance
  const currentPlatform = useMemo(() => getCurrentPlatform(), [getCurrentPlatform]);
  const platformString = useMemo(() => getPlatformString(), [getPlatformString]);
  const dateTimeOffset = useMemo(() => getDateTimeOffset(), [getDateTimeOffset]);

  return {
    // Methods
    isSetSupported,
    getDateTimeOffset,
    getCurrentPlatform,
    getPlatformString,
    closeContainer,
    
    // Memoized values
    currentPlatform,
    platformString,
    dateTimeOffset,
    
    // Static methods converted to functions
    isOnPremSystem: () => Office.context.mailbox.userProfile.accountType === "enterprise",
    getSpecificVersion: () => {
      let isStopCheck = false;
      let baseVersion = 9;
      while (!isStopCheck) {
        if (Office.context.requirements.isSetSupported('Mailbox', '1.' + baseVersion.toString()) || baseVersion === 0) {
          isStopCheck = true;
        }
        baseVersion--;
      }
      return baseVersion;
    },
    getRunningPlatform: (): Office.PlatformType => {
      if (Office.context.diagnostics) {
        return Office.context.diagnostics.platform;
      } else {
        return Office.PlatformType.PC;
      }
    }
  };
};

// Hook for checking if running on on-premises system
export const useOnPremSystem = () => {
  return useMemo(() => {
    return Office.context.mailbox.userProfile.accountType === "enterprise";
  }, []);
};

// Hook for getting specific version
export const useSpecificVersion = () => {
  return useMemo(() => {
    let isStopCheck = false;
    let baseVersion = 9;
    while (!isStopCheck) {
      if (Office.context.requirements.isSetSupported('Mailbox', '1.' + baseVersion.toString()) || baseVersion === 0) {
        isStopCheck = true;
      }
      baseVersion--;
    }
    return baseVersion;
  }, []);
};

// Hook for getting running platform
export const useRunningPlatform = () => {
  return useMemo((): Office.PlatformType => {
    if (Office.context.diagnostics) {
      return Office.context.diagnostics.platform;
    } else {
      return Office.PlatformType.PC;
    }
  }, []);
};
